/**
 * Loading a real store: the opening stock, a stock count, a new line, a
 * delivery with no order behind it, and an order raised somewhere else.
 *
 * The import shows what it WOULD do before anything is saved — line by line,
 * with what changes and what it could not read — because a storekeeper asked
 * to trust a file with four hundred rows in it will not, and should not.
 */
import { useState, type ChangeEvent } from "react";
import {
  FIELD_LABEL,
  INTAKE_COLUMNS,
  intakeSummary,
  planIntake,
  planReceipt,
  receiptSummary,
  type IntakePlan,
  type ReceiptPlan,
} from "../../domain/storesIntake";
import { categoriesOf, heldAt, type StockItem, type StoresRegister } from "../../domain/stores";
import {
  addItem,
  applyIntake,
  applyReceipt,
  recordExternalOrder,
  type Result,
} from "../../domain/storesEdit";
import { Button, Field, Input, Select, Table } from "../../design/components";
import { Dialog } from "./Purchasing";
import { t, tn } from "../../i18n";
import s from "./Stores.module.css";

const storeName = (reg: StoresRegister, code: string) =>
  reg.locations.find((l) => l.code === code)?.name ?? code;

/* ── loading the stock ────────────────────────────────────────────────── */

export function IntakeDialog({
  register,
  who,
  onClose,
  onApply,
}: {
  register: StoresRegister;
  who: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [location, setLocation] = useState(register.locations[0]?.code ?? "");
  const [basis, setBasis] = useState<"opening" | "count">("opening");
  const [plan, setPlan] = useState<IntakePlan | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const read = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (/\.xlsx?$/i.test(f.name)) {
      setFailed(
        t("An Excel workbook cannot be read directly. In Excel choose Save As › CSV, or copy the rows and paste them below."),
      );
      return;
    }
    setFailed(null);
    const body = await f.text();
    setText(body);
    setSource(f.name);
    setPlan(planIntake(register, body, { location, locationLabel: storeName(register, location) }));
  };

  const check = () => {
    setFailed(null);
    const name = source || t("rows pasted on {date}", { date: new Date().toISOString().slice(0, 10) });
    setSource(name);
    setPlan(planIntake(register, text, { location, locationLabel: storeName(register, location) }));
  };

  return (
    <Dialog onClose={onClose} testId="stores-intake-dialog" title={t("Load the stock")}>
      <p className={s.meta}>
        {t(
          "Save your store spreadsheet as CSV and choose it, or paste the rows below. Nothing is saved until you review it.",
        )}
      </p>

      <div className={s.formRow}>
        <Field label={t("Is this the opening stock, or a stock count?")}>
          <Select
            value={basis}
            onChange={(e) => setBasis(e.target.value as "opening" | "count")}
            aria-label={t("Is this the opening stock, or a stock count?")}
            data-testid="stores-intake-basis"
          >
            <option value="opening">{t("The opening stock — this is what we hold")}</option>
            <option value="count">{t("A stock count — we walked the shelves")}</option>
          </Select>
        </Field>
        <Field label={t("Into which store")}>
          <Select value={location} onChange={(e) => setLocation(e.target.value)} aria-label={t("Into which store")}>
            {register.locations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <p className={s.meta}>
        {basis === "opening"
          ? t(
              "An opening balance says what is on the shelves. It is not evidence about anything, so it does not touch the count-accuracy figure.",
            )
          : t(
              "A stock count is evidence: the differences it finds are real discrepancies, and the count-accuracy figure on the desk is computed from these and nothing else.",
            )}
      </p>

      <Field label={t("Choose a CSV file")}>
        <input type="file" accept=".csv,text/csv,text/plain" onChange={read} aria-label={t("Choose a CSV file")} />
      </Field>
      <Field label={t("Or paste the rows, with their heading row")}>
        <textarea
          className={s.textarea}
          rows={6}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"sku,name,category,unit,qty,min,max,unit cost,supplier\nEL-1001,Runway edge lamp,electrical,each,64,40,120,385,Lighting supplier"}
          aria-label={t("Or paste the rows, with their heading row")}
          data-testid="stores-intake-text"
        />
      </Field>
      <details>
        <summary className={s.meta}>{t("Which column headings are understood")}</summary>
        <ul className={s.bins}>
          {INTAKE_COLUMNS.map((c) => (
            <li key={c.key}>
              <b>{t(c.label)}</b>
              {": "}
              <span className={s.why}>{c.names.slice(0, 5).join(" · ")}</span>
            </li>
          ))}
        </ul>
        <p className={s.meta}>
          {t("Only the catalog number is required, and a name for anything the catalog has not seen before.")}
        </p>
      </details>

      {failed && (
        <p className={s.refused} role="alert">
          {failed}
        </p>
      )}

      {plan && <IntakePreview plan={plan} register={register} />}

      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button onClick={check} data-testid="stores-intake-check">
          {t("Show me what it would do")}
        </Button>
        <Button
          variant="primary"
          disabled={!plan || plan.rows.length === 0 || Boolean(plan.problem)}
          data-testid="stores-intake-apply"
          onClick={() => {
            if (!plan) return;
            void onApply(applyIntake(register, plan, { basis, source }, { name: who }));
            onClose();
          }}
        >
          {basis === "count" ? t("Record the count") : t("Load it in")}
        </Button>
      </div>
    </Dialog>
  );
}

function IntakePreview({ plan, register }: { plan: IntakePlan; register: StoresRegister }) {
  if (plan.problem) {
    return (
      <p className={s.refused} role="alert" data-testid="stores-intake-problem">
        {plan.problem}
      </p>
    );
  }
  return (
    <div data-testid="stores-intake-plan">
      <p className={s.note}>{intakeSummary(plan)}</p>
      {plan.ignored.length > 0 && (
        <p className={s.meta}>
          {t("These columns were not recognized and nothing was taken from them: {list}", {
            list: plan.ignored.join(", "),
          })}
        </p>
      )}
      <Table>
        <thead>
          <tr>
            <th>{t("Line")}</th>
            <th>{t("Catalog number")}</th>
            <th>{t("What would happen")}</th>
          </tr>
        </thead>
        <tbody>
          {plan.rows.slice(0, 60).map((r) => (
            <tr key={r.line}>
              <td className={s.when}>{r.line}</td>
              <td className={s.sku}>
                {r.sku || <span className={s.unknown}>{t("none")}</span>}
                <div className={s.why}>{r.name}</div>
              </td>
              <td>
                {r.status === "error" ? (
                  <span className={s.bad}>
                    {r.errors.map((e) => (
                      <div key={e.message}>
                        {e.field ? `${t(FIELD_LABEL[e.field])}: ` : ""}
                        {e.message}
                        {e.value ? ` — "${e.value}"` : ""}
                      </div>
                    ))}
                  </span>
                ) : r.status === "new" ? (
                  <span className={s.good}>
                    {t("new to the catalog")}
                    {r.qty !== undefined && ` · ${r.qty}`}
                  </span>
                ) : r.status === "same" ? (
                  <span className={s.unknown}>{t("already as recorded")}</span>
                ) : (
                  <>
                    {r.changes.map((c) => (
                      <div key={c.label}>
                        {t(c.label)}: <span className={s.unknown}>{c.before}</span> → <b>{c.after}</b>
                      </div>
                    ))}
                    {r.changes.some((c) => c.field === "qty") && (r.elsewhere?.length ?? 0) > 0 && (
                      /* the total will move by more than this row shows, and that is not a surprise anybody should get afterwards */
                      <div className={s.why}>
                        {t("also held elsewhere and untouched by this: {list}", {
                          list: r.elsewhere!.map((e) => `${e.qty} in ${storeName(register, e.location)}`).join(", "),
                        })}
                      </div>
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {plan.rows.length > 60 && (
        <p className={s.meta}>{t("{n} more lines are not shown here; all of them will be applied.", { n: plan.rows.length - 60 })}</p>
      )}
    </div>
  );
}

/* ── one new line, typed ──────────────────────────────────────────────── */

export function NewLineDialog({
  register,
  who,
  onClose,
  onApply,
}: {
  register: StoresRegister;
  who: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const [f, setF] = useState({
    sku: "",
    name: "",
    category: "consumable",
    uom: "each",
    qty: "0",
    min: "0",
    max: "0",
    unitCost: "",
    leadDays: "",
    supplier: "",
    location: register.locations[0]?.code ?? "",
    bin: "",
    criticalWhy: "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));

  return (
    <Dialog onClose={onClose} testId="stores-new-line-dialog" title={t("Add a line to the catalog")}>
      <div className={s.formRow}>
        <Field label={t("Catalog number")}>
          <Input value={f.sku} onChange={set("sku")} aria-label={t("Catalog number")} data-testid="new-line-sku" />
        </Field>
        <Field label={t("Name")}>
          <Input value={f.name} onChange={set("name")} aria-label={t("Name")} data-testid="new-line-name" />
        </Field>
        <Field label={t("Category")}>
          <Select value={f.category} onChange={set("category")} aria-label={t("Category")}>
            {categoriesOf(register).map((c) => (
              <option key={c.key} value={c.key}>
                {t(c.label)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("Unit")}>
          <Input value={f.uom} onChange={set("uom")} aria-label={t("Unit")} placeholder="each" />
        </Field>
        <Field label={t("Quantity on the shelf now")}>
          <Input type="number" value={f.qty} onChange={set("qty")} aria-label={t("Quantity on the shelf now")} />
        </Field>
        <Field label={t("Store")}>
          <Select value={f.location} onChange={set("location")} aria-label={t("Store")}>
            {register.locations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("Bin")}>
          <Input value={f.bin} onChange={set("bin")} aria-label={t("Bin")} />
        </Field>
        <Field label={t("Reorder point")}>
          <Input type="number" value={f.min} onChange={set("min")} aria-label={t("Reorder point")} />
        </Field>
        <Field label={t("Maximum")}>
          <Input type="number" value={f.max} onChange={set("max")} aria-label={t("Maximum")} />
        </Field>
        <Field label={t("Unit cost")}>
          <Input type="number" value={f.unitCost} onChange={set("unitCost")} aria-label={t("Unit cost")} />
        </Field>
        <Field label={t("Lead time (days)")}>
          <Input type="number" value={f.leadDays} onChange={set("leadDays")} aria-label={t("Lead time (days)")} />
        </Field>
        <Field label={t("Supplier")}>
          <Input value={f.supplier} onChange={set("supplier")} aria-label={t("Supplier")} />
        </Field>
      </div>
      <Field label={t("If it is a critical spare, why")}>
        <Input
          value={f.criticalWhy}
          onChange={set("criticalWhy")}
          aria-label={t("If it is a critical spare, why")}
          placeholder={t("what stops if it is not held")}
        />
      </Field>
      <p className={s.meta}>
        {t("A criticality with no reason against it is the first thing a cost review deletes, so this asks for one.")}
      </p>
      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          data-testid="new-line-save"
          onClick={() => {
            const item: Omit<StockItem, "id"> = {
              sku: f.sku,
              name: f.name,
              category: f.category as StockItem["category"],
              uom: f.uom || "each",
              unitCost: num(f.unitCost),
              min: Number(f.min) || 0,
              max: Number(f.max) || 0,
              leadDays: num(f.leadDays),
              supplier: f.supplier || undefined,
              critical: f.criticalWhy.trim() ? { why: f.criticalWhy.trim() } : undefined,
              bins: [{ location: f.location, bin: f.bin || undefined, onHand: Number(f.qty) || 0 }],
            };
            void onApply(addItem(register, item, { name: who }));
            onClose();
          }}
        >
          {t("Add it")}
        </Button>
      </div>
    </Dialog>
  );
}

/* ── a delivery with no order, and an order raised elsewhere ──────────── */

/** One editable line of an order or a delivery, typed rather than pasted. */
interface Line {
  key: number;
  sku: string;
  qty: string;
  unitCost: string;
  /* for a part that is not in the catalogue: what to create it as */
  newSku?: string;
  newName?: string;
  newCategory?: string;
}

/** The picker's own value for "this part is not on the list". */
const NOT_LISTED = "__not_listed__";

let lineKey = 0;
const blankLine = (sku: string): Line => ({ key: (lineKey += 1), sku, qty: "1", unitCost: "" });

function LineEditor({
  register,
  lines,
  setLines,
  idPrefix,
  showCost = true,
  allowNew = false,
}: {
  register: StoresRegister;
  lines: Line[];
  setLines: (next: Line[]) => void;
  idPrefix: string;
  showCost?: boolean;
  /** a delivery may bring a part the catalogue has never held; an order may not */
  allowNew?: boolean;
}) {
  const items = register.items.filter((i) => !i.obsoleted);
  const set = (key: number, field: keyof Line, value: string) =>
    setLines(lines.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>{t("Line")}</th>
            <th className={s.num}>{t("Quantity")}</th>
            {showCost && <th className={s.num}>{t("Unit cost")}</th>}
            <th />
          </tr>
        </thead>
        <tbody data-testid={`${idPrefix}-lines`}>
          {lines.map((l, i) => (
            <tr key={l.key}>
              <td>
                <Select
                  value={l.sku}
                  onChange={(e) => set(l.key, "sku", e.target.value)}
                  aria-label={t("Line {n}", { n: i + 1 })}
                  data-testid={`${idPrefix}-sku-${i}`}
                >
                  {items.map((it) => (
                    <option key={it.sku} value={it.sku}>
                      {`${it.sku} — ${it.name}`}
                    </option>
                  ))}
                  {allowNew && <option value={NOT_LISTED}>{t("— a part that is not on the list —")}</option>}
                </Select>
                {l.sku === NOT_LISTED && (
                  <div className={s.formRow} data-testid={`${idPrefix}-new-${i}`}>
                    <Field label={t("Catalog number")}>
                      <Input
                        value={l.newSku ?? ""}
                        onChange={(e) => set(l.key, "newSku", e.target.value)}
                        aria-label={t("Catalog number, line {n}", { n: i + 1 })}
                        data-testid={`${idPrefix}-newsku-${i}`}
                      />
                    </Field>
                    <Field label={t("Name")}>
                      <Input
                        value={l.newName ?? ""}
                        onChange={(e) => set(l.key, "newName", e.target.value)}
                        aria-label={t("Name, line {n}", { n: i + 1 })}
                        data-testid={`${idPrefix}-newname-${i}`}
                      />
                    </Field>
                    <Field label={t("Category")}>
                      <Select
                        value={l.newCategory ?? categoriesOf(register)[0].key}
                        onChange={(e) => set(l.key, "newCategory", e.target.value)}
                        aria-label={t("Category, line {n}", { n: i + 1 })}
                      >
                        {categoriesOf(register).map((c) => (
                          <option key={c.key} value={c.key}>
                            {t(c.label)}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>
                )}
              </td>
              <td className={s.num}>
                <Input
                  type="number"
                  value={l.qty}
                  onChange={(e) => set(l.key, "qty", e.target.value)}
                  aria-label={t("Quantity, line {n}", { n: i + 1 })}
                  data-testid={`${idPrefix}-qty-${i}`}
                />
              </td>
              {showCost && (
                <td className={s.num}>
                  <Input
                    type="number"
                    value={l.unitCost}
                    onChange={(e) => set(l.key, "unitCost", e.target.value)}
                    aria-label={t("Unit cost, line {n}", { n: i + 1 })}
                    data-testid={`${idPrefix}-cost-${i}`}
                  />
                </td>
              )}
              <td>
                {lines.length > 1 && (
                  <Button size="sm" onClick={() => setLines(lines.filter((x) => x.key !== l.key))}>
                    {t("Remove")}
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button
        size="sm"
        data-testid={`${idPrefix}-add`}
        onClick={() => setLines([...lines, blankLine(items[0]?.sku ?? "")])}
      >
        {t("Add another line")}
      </Button>
    </>
  );
}

const toLines = (lines: Line[]) =>
  lines
    .map((l) => ({ sku: l.sku, qty: Number(l.qty), unitCost: l.unitCost.trim() ? Number(l.unitCost) : undefined }))
    .filter((l) => l.sku && l.sku !== NOT_LISTED && Number.isFinite(l.qty) && l.qty > 0);

/** The same, with a not-listed row resolved to the number and name typed for it. */
const typedLines = (lines: Line[]) =>
  lines
    .map((l) =>
      l.sku === NOT_LISTED
        ? {
            sku: (l.newSku ?? "").trim(),
            qty: Number(l.qty),
            unitCost: l.unitCost.trim() ? Number(l.unitCost) : undefined,
            newName: (l.newName ?? "").trim(),
            newCategory: l.newCategory,
          }
        : { sku: l.sku, qty: Number(l.qty), unitCost: l.unitCost.trim() ? Number(l.unitCost) : undefined, newName: undefined, newCategory: undefined },
    )
    .filter((l) => l.sku && Number.isFinite(l.qty) && l.qty > 0);

export function ExternalOrderDialog({
  register,
  who,
  onClose,
  onApply,
}: {
  register: StoresRegister;
  who: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const first = register.items.find((i) => !i.obsoleted)?.sku ?? "";
  const [number, setNumber] = useState("");
  const [system, setSystem] = useState("");
  const [supplier, setSupplier] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [lines, setLines] = useState<Line[]>([blankLine(first)]);

  return (
    <Dialog onClose={onClose} testId="stores-external-dialog" title={t("Record an order raised elsewhere")}>
      <p className={s.meta}>
        {t(
          "An order raised in procurement's own system. It's recorded here, marked with its source, so deliveries can be booked against it.",
        )}
      </p>
      <div className={s.formRow}>
        <Field label={t("Order number, as that system carries it")}>
          <Input value={number} onChange={(e) => setNumber(e.target.value)} aria-label={t("Order number, as that system carries it")} data-testid="external-number" />
        </Field>
        <Field label={t("Raised in which system")}>
          <Input value={system} onChange={(e) => setSystem(e.target.value)} placeholder="SAP" aria-label={t("Raised in which system")} data-testid="external-system" />
        </Field>
        <Field label={t("Supplier")}>
          <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} aria-label={t("Supplier")} data-testid="external-supplier" />
        </Field>
        <Field label={t("Expected")}>
          <Input type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} aria-label={t("Expected")} />
        </Field>
      </div>
      <LineEditor register={register} lines={lines} setLines={setLines} idPrefix="external" />
      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          data-testid="external-save"
          onClick={() => {
            void onApply(
              recordExternalOrder(
                register,
                { number, supplier, system, expectedAt: expectedAt || undefined, lines: toLines(lines) },
                { name: who },
              ),
            );
            onClose();
          }}
        >
          {tn(toLines(lines).length, "Record it ({n} line)", "Record it ({n} lines)")}
        </Button>
      </div>
    </Dialog>
  );
}

/* ── booking a delivery in: typed, or read from the supplier's own list ── */

export function DeliveryDialog({
  register,
  who,
  orderNumber,
  onClose,
  onApply,
}: {
  register: StoresRegister;
  who: string;
  /** set when booking in against an order the register holds */
  orderNumber?: string;
  onClose: () => void;
  onApply: (r: Result) => void | Promise<void>;
}) {
  const order = orderNumber ? register.orders.find((o) => o.number === orderNumber) : undefined;
  const outstanding = (order?.lines ?? []).filter((l) => l.received < l.qty);
  const first = outstanding[0]?.sku ?? register.items.find((i) => !i.obsoleted)?.sku ?? "";

  const [how, setHow] = useState<"typed" | "pasted">("typed");
  const [location, setLocation] = useState(register.locations[0]?.code ?? "");
  /* until somebody picks a store, a line goes where that line already lives */
  const [storeChosen, setStoreChosen] = useState(false);
  const [supplier, setSupplier] = useState(order?.supplier ?? "");
  const [deliveryNote, setDeliveryNote] = useState("");
  const [reason, setReason] = useState("");
  const [lines, setLines] = useState<Line[]>(
    outstanding.length
      ? outstanding.map((l) => ({ key: (lineKey += 1), sku: l.sku, qty: String(l.qty - l.received), unitCost: "" }))
      : [blankLine(first)],
  );
  const [text, setText] = useState("");
  const [plan, setPlan] = useState<ReceiptPlan | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  const read = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (/\.xlsx?$/i.test(f.name)) {
      setFailed(t("An Excel workbook cannot be read directly. In Excel choose Save As › CSV, or copy the rows and paste them below."));
      return;
    }
    setFailed(null);
    const body = await f.text();
    setText(body);
    setDeliveryNote((d) => d || f.name);
    setPlan(planReceipt(register, body, { location, locationLabel: storeName(register, location), orderNumber, storeChosen, allowNew: true }));
  };

  const check = () => {
    setFailed(null);
    setPlan(planReceipt(register, text, { location, locationLabel: storeName(register, location), orderNumber, storeChosen, allowNew: true }));
  };

  const typedPlan = (): ReceiptPlan => {
    const rows = typedLines(lines).map((l, i) => {
      const item = register.items.find((x) => x.sku === l.sku);
      /* the same rule as the pasted path: it joins the stock it belongs with */
      const places = item ? [...new Set(item.bins.map((b) => b.location))] : [];
      const where = storeChosen || places.length !== 1 ? location : places[0];
      const before = item ? heldAt(item, where) : 0;
      return {
        line: i + 1,
        sku: l.sku,
        name: item?.name ?? l.newName ?? "",
        item: item
          ? undefined
          : {
              sku: l.sku,
              name: l.newName ?? "",
              category: l.newCategory ?? categoriesOf(register)[0].key,
              uom: "each",
              unitCost: l.unitCost,
              min: 0,
              max: 0,
              bins: [],
            },
        qty: l.qty,
        unitCost: l.unitCost,
        location: where,
        status: (item ? "ready" : "new") as "ready" | "new",
        errors: [],
        before,
        after: before + l.qty,
        elsewhere: item
          ? item.bins.filter((b) => b.location !== where && b.onHand > 0).map((b) => ({ location: b.location, qty: b.onHand }))
          : undefined,
      };
    });
    return {
      rows,
      ignored: [],
      counts: { ready: rows.filter((r) => r.status === "ready").length, error: 0, new: rows.filter((r) => r.status === "new").length },
      units: rows.reduce((n, r) => n + (r.qty ?? 0), 0),
    };
  };

  return (
    <Dialog
      onClose={onClose}
      testId="stores-delivery-dialog"
      title={order ? t("Book in {number}", { number: order.number }) : t("Book in a delivery")}
    >
      {order ? (
        <p className={s.meta}>{t("From {supplier}", { supplier: order.supplier })}</p>
      ) : (
        <p className={s.meta}>
          {t(
            "A delivery with no order behind it — a direct purchase, an emergency buy, free issue from a contractor, a transfer in from another airport. Every line is ADDED to what the shelf already holds.",
          )}
        </p>
      )}

      <div className={s.formRow}>
        <Field label={t("How")}>
          <Select value={how} onChange={(e) => setHow(e.target.value as "typed" | "pasted")} aria-label={t("How")} data-testid="delivery-how">
            <option value="typed">{t("Enter the lines here")}</option>
            <option value="pasted">{t("Read them from a spreadsheet")}</option>
          </Select>
        </Field>
        <Field label={t("Into which store")}>
          <Select
            value={location}
            onChange={(e) => {
              setLocation(e.target.value);
              setStoreChosen(true);
            }}
            aria-label={t("Into which store")}
            data-testid="delivery-store"
          >
            {register.locations.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("Delivery note")}>
          <Input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} aria-label={t("Delivery note")} data-testid="delivery-note" />
        </Field>
        {!order && (
          <Field label={t("Supplier")}>
            <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} aria-label={t("Supplier")} />
          </Field>
        )}
      </div>

      {how === "typed" ? (
        <LineEditor register={register} lines={lines} setLines={setLines} idPrefix="delivery" allowNew />
      ) : (
        <>
          <Field label={t("Choose a CSV file")}>
            <input type="file" accept=".csv,text/csv,text/plain" onChange={read} aria-label={t("Choose a CSV file")} />
          </Field>
          <Field label={t("Or paste the delivery, with its heading row")}>
            <textarea
              className={s.textarea}
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"sku,qty,unit cost\nEL-1001,40,385"}
              aria-label={t("Or paste the delivery, with its heading row")}
              data-testid="delivery-text"
            />
          </Field>
          <Button data-testid="delivery-check" onClick={check}>
            {t("Show me what it would do")}
          </Button>
          {plan && <ReceiptPreview plan={plan} register={register} />}
        </>
      )}

      {!order && (
        <Field label={t("Why it came in without an order (required)")}>
          <textarea
            className={s.textarea}
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-label={t("Why it came in without an order (required)")}
            data-testid="delivery-reason"
          />
        </Field>
      )}
      {failed && (
        <p className={s.refused} role="alert">
          {failed}
        </p>
      )}
      <p className={s.meta}>
        {t("Each line is added to what that store already holds, and the catalog shows the new figure straight away.")}
      </p>

      <div className={s.formFoot}>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button
          variant="primary"
          data-testid="delivery-save"
          onClick={() => {
            const use = how === "typed" ? typedPlan() : plan;
            if (!use) {
              setFailed(t("Read the spreadsheet first, so you can see what it would do."));
              return;
            }
            void onApply(
              applyReceipt(
                register,
                use,
                {
                  orderNumber,
                  supplier: supplier || undefined,
                  deliveryNote: deliveryNote || undefined,
                  reason,
                },
                { name: who },
              ),
            );
            onClose();
          }}
        >
          {t("Book it in")}
        </Button>
      </div>
    </Dialog>
  );
}

function ReceiptPreview({ plan, register }: { plan: ReceiptPlan; register: StoresRegister }) {
  if (plan.problem) {
    return (
      <p className={s.refused} role="alert" data-testid="delivery-problem">
        {plan.problem}
      </p>
    );
  }
  return (
    <div data-testid="delivery-plan">
      <p className={s.note}>{receiptSummary(plan)}</p>
      {plan.ignored.length > 0 && (
        <p className={s.meta}>
          {t("These columns were not recognized and nothing was taken from them: {list}", { list: plan.ignored.join(", ") })}
        </p>
      )}
      <Table>
        <thead>
          <tr>
            <th>{t("Line")}</th>
            <th className={s.num}>{t("Delivered")}</th>
            <th>{t("Into")}</th>
            <th className={s.num}>{t("On that shelf")}</th>
            <th className={s.num}>{t("After")}</th>
          </tr>
        </thead>
        <tbody>
          {plan.rows.slice(0, 60).map((r) => (
            <tr key={r.line}>
              <td>
                <span className={s.sku}>{r.sku}</span>
                <div className={s.why}>{r.name}</div>
                {r.status === "new" && <div className={s.good}>{t("new to the catalog")}</div>}
              </td>
              <td className={s.num}>
                {r.status === "error" ? (
                  <span className={s.bad}>
                    {r.errors.map((e) => (
                      <div key={e.message}>
                        {e.message}
                        {e.value ? ` — "${e.value}"` : ""}
                      </div>
                    ))}
                  </span>
                ) : (
                  r.qty
                )}
              </td>
              <td>
                {storeName(register, r.location)}
                {(r.elsewhere?.length ?? 0) > 0 && (
                  <div className={s.why}>
                    {t("also held elsewhere and untouched by this: {list}", {
                      list: r.elsewhere!.map((e) => `${e.qty} in ${storeName(register, e.location)}`).join(", "),
                    })}
                  </div>
                )}
              </td>
              <td className={s.num}>{r.before ?? "—"}</td>
              <td className={s.num}>{r.status === "error" ? "—" : <b>{r.after}</b>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

export { tn };
